import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { axiosGet } from "../../../utils/AxiosApi";
import { checkRolePermissions, URL } from "../../../utils/Constants";
import "../../../assets/scss/formbuilder.scss";
import moment from "moment/moment";
import { Edit, Visibility, Send } from "@mui/icons-material";
import ModalWindow from "../../UI/ModalWindow";
import DynamicFormDisplay from "./DynamicFormDisplay";
import Dynamicformbuilder from "./Dynamicformbuilder";

const ROLE_CODE_TO_ROLE = { RL_ADM: "management", RL_STF: "teacher", RL_STD: "student" };
const ROLE_CODE_TO_LABEL = { RL_ADM: "Management", RL_STF: "Teacher", RL_STD: "Student" };

const ActionBtn = ({ title, icon, onClick, color = "#475569" }) => (
  <button title={title} onClick={onClick} className="btn btn-sm p-1"
    style={{ color, lineHeight: 1, border: "none", background: "transparent" }}>{icon}</button>
);

const RoleBadge = ({ role }) => {
  const map = { student: { bg: "#f5f3ff", color: "#7c3aed" }, teacher: { bg: "#f0f9ff", color: "#0369a1" }, management: { bg: "#fef2f2", color: "#b91c1c" } };
  const s = map[role?.toLowerCase()] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span className="badge rounded-pill px-2 py-1" style={{ background: s.bg, color: s.color, fontSize: "0.72rem", fontWeight: 600 }}>{role}</span>
  );
};

const ListDynamicForm = () => {
  const [loading, setLoading] = useState(false);
  const [listForms, setListForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  // refresh list after edit success
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchForms = useCallback(() => {
    setLoading(true);
    axiosGet(URL.listDynamicForm, (response) => {
      if (response.status === 200) setListForms(response.data.data);
      setLoading(false);
    });
  }, []);

  const reloadList = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => { fetchForms(); }, [fetchForms, refreshKey]);

  const normalizeForm = (form) => ({
    ...form,
    questions: form?.questions?.map((q) => {
      let parsedOptions = q.options;
      if (typeof q.options === "string") {
        try { parsedOptions = JSON.parse(q.options); }
        catch { parsedOptions = []; }
      }
      return { ...q, options: parsedOptions ?? [] };
    }),
  });

  const selectForm = (form, action) => {
    setSelectedForm(normalizeForm(form));
    if (action === "edit") setShowEdit(true);
    if (action === "preview") setShowPreview(true);
    if (action === "submit") setShowSubmit(true);
  };

  const getRoleLabel = (code) => {
    const role = Object.keys(ROLE_CODE_TO_ROLE).find(k => ROLE_CODE_TO_LABEL[ROLE_CODE_TO_ROLE[k]] === ROLE_CODE_TO_LABEL[code]);
    return ROLE_CODE_TO_LABEL[code] || "-";
  };

  const columns = [
    { name: "Title", selector: (row) => row.title, sortable: true, cell: (row) => <span className="fw-semibold">{row.title}</span> },
    { name: "Description", selector: (row) => row.description || "—", hide: "sm" },
    {
      name: "Evaluator", selector: (row) => row.evaluatorRoleCode,
      cell: (row) => row.evaluatorRoleCode ? <RoleBadge role={ROLE_CODE_TO_LABEL[row.evaluatorRoleCode] || row.evaluatorRoleCode} /> : "—", hide: "md",
    },
    {
      name: "Evaluatee", selector: (row) => row.evaluateeRoleCode,
      cell: (row) => row.evaluateeRoleCode ? <RoleBadge role={ROLE_CODE_TO_LABEL[row.evaluateeRoleCode] || row.evaluateeRoleCode} /> : "—", hide: "md",
    },
    {
      name: "Status", selector: (row) => row.isOpen,
      cell: (row) => (
        <span className={`badge rounded-pill px-2 py-1 ${row.isOpen ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}
          style={{ fontSize: "0.72rem", fontWeight: 600 }}>{row.isOpen ? "Open" : "Closed"}</span>
      ),
    },
    { name: "Validity", selector: (row) =>
    {
      if (row.openDate) {
        const openDate = moment(row.openDate).format("YYYY-MM-DD");
        const open = moment(openDate).startOf('day');
        const now = moment();
        if (open.isAfter(now)) return (
          <>
            {openDate}
            <br />
            <span className={'badge rounded-pill px-2 py-1 bg-warning text-white'}
              style={{ fontSize: "0.6rem", fontWeight: 600 }}>Opens in {open.fromNow(true)}</span>
          </>
        )
      }

      if(!row.closeDate) return "No expiry";
      const closeDate = moment(row.closeDate).format("YYYY-MM-DD");
      const close = moment(closeDate).endOf('day');
      const now = moment();

      if (close.isBefore(now)) return "Expired";

      return (
        <>
          {closeDate}
          <br/>
          <span className={'badge rounded-pill px-2 py-1 bg-success text-white'}
            style={{ fontSize: "0.6rem", fontWeight: 600 }}>{close.fromNow(true)} remaining</span>
        </>
      );
    },
      sortFunction: (a, b) => {
        return moment(a.closeDate).endOf('day').valueOf() -
          moment(b.closeDate).endOf('day').valueOf();
      }, sortable: true, hide: "sm" },
    {
      name: "Actions", ignoreRowClick: true, button: true,
      cell: (row) => (
        <div className="d-flex gap-1">
          {checkRolePermissions("create-form", "activity") && (
            <>
              <ActionBtn title="Edit"    icon={<Edit fontSize="small" />}       onClick={() => selectForm(row, "edit")}    color="#4285f4" />
              <ActionBtn title="Preview" icon={<Visibility fontSize="small" />} onClick={() => selectForm(row, "preview")} color="#64748b" />
            </>
          )}
          <ActionBtn title="Submit" icon={<Send fontSize="small" />} onClick={() => selectForm(row, "submit")} color="#10b981" />
        </div>
      ),
    },
  ];

  const formMeta = selectedForm ? {
    id: selectedForm.id,
    respondentRole: ROLE_CODE_TO_ROLE[selectedForm.evaluatorRoleCode] || null,
    evaluateeRole: ROLE_CODE_TO_ROLE[selectedForm.evaluateeRoleCode] || null,
    allowSelfEvaluation: selectedForm.allowSelfEvaluation,
  } : null;

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    return moment(dateStr).format("YYYY-MM-DD");
  };

  return (
    <>
      <div className="content-body">
        <div className="card border-0 shadow-sm" style={{ borderRadius: 8 }}>
          <div className="card-body p-3">
            <DataTable dense columns={columns} data={listForms} pagination progressPending={loading}
              noDataComponent={<div className="text-center text-muted py-4">No forms found.</div>}
              customStyles={{
                headCells: { style: { fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#64748b" } },
                rows: { style: { fontSize: "0.9rem" } },
              }} />
          </div>
        </div>
      </div>

      <ModalWindow modal={showPreview} size="lg" toggleModal={() => setShowPreview(false)} modalHeader="Preview"
        modalBody={
          <DynamicFormDisplay previewOnly title={selectedForm?.title || ""} description={selectedForm?.description || ""}
            items={selectedForm?.questions || []} formMeta={formMeta} />
        } />

      <ModalWindow modal={showSubmit} size="lg" toggleModal={() => setShowSubmit(false)} modalHeader="Submit Form"
        modalBody={
          <DynamicFormDisplay previewOnly={false} title={selectedForm?.title || ""} description={selectedForm?.description || ""}
            items={selectedForm?.questions || []} formMeta={formMeta} />
        } />

      <ModalWindow modal={showEdit} size="lg" toggleModal={() => setShowEdit(false)} modalHeader="Edit Form"
        modalBody={
          <Dynamicformbuilder id={selectedForm?.id} title={selectedForm?.title || ""} description={selectedForm?.description || ""}
            showEdit={showEdit}
            items={selectedForm?.questions || []} evaluatorRoleCode={selectedForm?.evaluatorRoleCode}
            evaluateeRoleCode={selectedForm?.evaluateeRoleCode} isOpen={selectedForm?.isOpen}
            openDate={formatDateForInput(selectedForm?.openDate)} closeDate={formatDateForInput(selectedForm?.closeDate)}
            onSuccess={reloadList}
            onClose={() => setShowEdit(false)} />
        } />
    </>
  );
};

export default ListDynamicForm;
